// ResultsTab.js — NEW component
// Shows the domain check verdict (pass/fail) and findings extracted from the raw API response:
//   verdict  ← data.web_presence.recommendation
//   findings ← data.web_presence.rawData.risks.{ validity[], notice[], risk[] }
// Monitoring toggle and manual check/refresh controls are kept here (moved from DomainInfoTab).

import React, { useState, useRef, useEffect } from "react";
import { Card, List, Typography, Button, Switch, Tooltip, Empty } from "antd";
import {
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import { API_DOMAIN_MONITOR } from "../../../config/config";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

const { Text, Title } = Typography;

// Icon per risks sub-array key: validity = green, notice = orange, risk = red
const severityIconMap = {
    validity: <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />,
    notice: <ExclamationCircleOutlined style={{ color: "#fa8c16", fontSize: 20 }} />,
    risk: <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />,
    default: <ExclamationCircleOutlined style={{ color: "#fa8c16", fontSize: 20 }} />,
};

const severityColorMap = {
    validity: "#52c41a",
    notice: "#fa8c16",
    risk: "#ff4d4f",
    default: "#fa8c16",
};

const ResultsTab = ({ data, onRefresh, t = (key) => key }) => {
    // Monitoring toggle and manual check loading states (ported from DomainInfoTab)
    const [toggleLoading, setToggleLoading] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Manually trigger a domain check and refresh the detail data
    const handleCheck = async () => {
        if (isMounted.current) setCheckLoading(true);
        try {
            await axios.post(
                `${API_DOMAIN_MONITOR}/v1/domains/${data.id}/check`,
                {},
                {
                    headers: {
                        Authorization: "Bearer " + Cookies.get("dm_token"),
                    }
                }
            );
            if (isMounted.current) onRefresh();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("domain_monitor.error"),
                text: error.response?.data?.message || error.message || t("domain_monitor.unexpected_error")
            });
        } finally {
            if (isMounted.current) setCheckLoading(false);
        }
    };

    // Start or stop monitoring for this domain with a confirmation dialog
    const handleToggleMonitoring = async (checked) => {
        const endpoint = checked ? "start" : "stop";
        const result = await Swal.fire({
            title: t("domain_monitor.confirm_action"),
            text: checked ? t("domain_monitor.start_monitoring") : t("domain_monitor.stop_monitoring"),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t("domain_monitor.yes_proceed"),
            cancelButtonText: t("domain_monitor.cancel")
        });

        if (result.isConfirmed) {
            if (isMounted.current) setToggleLoading(true);
            try {
                await axios.patch(
                    `${API_DOMAIN_MONITOR}/v1/domains/${data.id}/${endpoint}`,
                    {},
                    {
                        headers: {
                            Authorization: "Bearer " + Cookies.get("dm_token"),
                        }
                    }
                );
                Swal.fire(t("domain_monitor.success"), t("domain_monitor.monitoring_updated_refresh"), "success");
                if (isMounted.current) onRefresh();
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: t("domain_monitor.error"),
                    text: error.response?.data?.message || error.message || t("domain_monitor.unexpected_error")
                });
            } finally {
                if (isMounted.current) setToggleLoading(false);
            }
        }
    };

    if (!data) return null;

    // verdict: pulled from web_presence.recommendation (e.g. "pass" / "fail")
    const verdict = data?.web_presence?.recommendation || data?.recommendation;
    const isPass = verdict?.toLowerCase() === "pass";

    // risks is an object with three sub-arrays:
    //   validity → legitimacy indicators (green)
    //   notice   → information to be aware of (orange)
    //   risk     → risk indicators (red)
    // Each item in those arrays has its own display_name, description, and category (e.g. "SOCIAL MEDIA").
    const rawRisks = data?.web_presence?.rawData?.risks || {};

    // _bucket is a separate key so it never clobbers any severity field the API may return on items
    const findings = [
        ...(rawRisks.validity || []).map((item) => ({ ...item, _bucket: "validity" })),
        ...(rawRisks.notice   || []).map((item) => ({ ...item, _bucket: "notice" })),
        ...(rawRisks.risk     || []).map((item) => ({ ...item, _bucket: "risk" })),
    ];

    return (
        <Card
            extra={
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Monitoring toggle — uses existing start/stop endpoints */}
                    <Switch
                        checked={data?.monitoringStatus === "active"}
                        loading={toggleLoading}
                        onChange={handleToggleMonitoring}
                        checkedChildren={t("domain_monitor.active")}
                        unCheckedChildren={t("domain_monitor.paused")}
                    />
                    {/* Manual check trigger — calls /domains/{id}/check */}
                    <Tooltip title={t("domain_monitor.refresh_tooltip")}>
                        <Button
                            icon={<ReloadOutlined spin={checkLoading} />}
                            onClick={handleCheck}
                            loading={checkLoading}
                        >
                            {t("domain_monitor.refresh")}
                        </Button>
                    </Tooltip>
                </div>
            }
        >
            {/* Verdict banner — only shown when API returns `verdict` or `result` field */}
            {verdict && (
                <div
                    style={{
                        background: isPass ? "#f6ffed" : "#fff1f0",
                        border: `1px solid ${isPass ? "#b7eb8f" : "#ffa39e"}`,
                        borderRadius: 4,
                        padding: "12px 20px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 20
                    }}
                >
                    {isPass
                        ? <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                        : <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
                    }
                    <Text style={{ color: isPass ? "#52c41a" : "#ff4d4f", fontWeight: 500, fontSize: 16 }}>
                        {verdict.charAt(0).toUpperCase() + verdict.slice(1).toLowerCase()}
                    </Text>
                </div>
            )}

            <Title level={5} style={{ marginBottom: 12 }}>{t("domain_monitor.tab_findings")}</Title>

            {/* Empty state clearly signals that `findings` is missing from the API response */}
            {findings.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <Text type="secondary">
                            No findings available — <code>web_presence.rawData.risks</code> is empty or not present in the API response.
                        </Text>
                    }
                />
            ) : (
                // Each finding uses: title/name, description, category, severity
                <List
                    bordered
                    dataSource={findings}
                    renderItem={(finding) => {
                        const icon = severityIconMap[finding._bucket] || severityIconMap.default;
                        const titleColor = severityColorMap[finding._bucket] || severityColorMap.default;
                        return (
                            <List.Item
                                style={{ alignItems: "flex-start" }}
                                extra={
                                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                        {finding.category?.toUpperCase()}
                                    </Text>
                                }
                            >
                                <List.Item.Meta
                                    avatar={<div style={{ marginTop: 2 }}>{icon}</div>}
                                    title={
                                        <Text style={{ color: titleColor }}>{finding.display_name || finding.name}</Text>
                                    }
                                    description={finding.description}
                                />
                            </List.Item>
                        );
                    }}
                />
            )}
        </Card>
    );
};

// --- DATA MAPPING ---
// verdict  ← data.web_presence.recommendation
// findings ← data.web_presence.rawData.risks.{ validity[], notice[], risk[] }
//            _bucket is the sub-array key: validity=green, notice=orange, risk=red
//            display_name/name, description, category come from each item in those arrays

export default ResultsTab;
